import React, { useState, useCallback } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import axios from "axios";
import useCookie from "../../hooks/useCookie";
import useCurrentUser from "../../hooks/useCurrentUser";
import { FileUploader } from "react-drag-drop-files";
import "./Generator.css";
import AdvancedOptions from "./AdvancedOptions";

// Elements for image-to-image generation

/**
 * Retrieves the image from the Stable Diffusion API
 * @param {*} prompt
 * @param {*} advanced
 * @param {*} negative_prompt
 * @param {*} seed
 * @param {*} guidance
 * @param {*} steps
 * @returns
 */
async function fetchImage(
    image,
    prompt,
    negative_prompt,
    seed,
    guidance,
    steps,
    csrftoken
) {
    let body;

    body = {
        "image": image,
        "prompt": prompt,
        "negative_prompt": negative_prompt,
        "steps": steps,
        "seed": seed,
        "cfg_scale": guidance,
    };

    let response;
    try {
        response = await axios.post(
            '/api/generate/image-to-image',
            body,
            {
                headers: {
                    "X-CSRFToken": csrftoken
                }
            }
        );
    } catch (error) {
        return error;
    }
    return response.data.images[0];
}

/**
 * Adds the generated image to the user's gallery
 * @param {*} user
 * @param {*} csrftoken
 * @param {*} prompt
 * @param {*} data
 */
async function addImageToGallery(user, csrftoken, prompt, data) {
    let recent = null;
    let newImage = {
        prompt: prompt,
        picture: data,
        studio: "AI_ART"
    };

    let userType;
    if (!user.is_staff) { // User is student
        userType = "students";
    } else if (!user.is_superuser) { // User is teacher
        userType = "teachers";
    } else { // User is admin
        userType = "admins";
    }

    // Get ID of user's recent gallery
    await axios.get(`/api/${userType}/${user.id}/`, {
        withCredentials: true,
    })
    .then((response) => {
        recent = response.data.recent;
        newImage["gallery"] = recent;
    });

    const headers = {
        "X-CSRFToken": csrftoken,
    };

    await axios
        .post(`/api/images/`, newImage, { headers })
        .then((response) => {
            showStatusPopup("Successful save!", "#430156");
        })
        .catch((error) => {
            console.log("Error while saving image: " + error);
            showStatusPopup("Error while saving image.", "#630c0c");
        })
        .finally(() => { // Enabled Save and Generate buttons again
            let saveImgBtn = document.getElementById("save-ai-img-btn");
            saveImgBtn.disabled = false;
            saveImgBtn.classList.remove("locked");

            let generateBtn = document.getElementById("GenerateButton");
            generateBtn.disabled = false;
            generateBtn.classList.remove("locked");

        });
}

// Wait for a specific amount of time
//      sleep: Amount of milliseconds to wait
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Show status popup in the middle of the screen.
//      text: Text to display.
//      color: Background color of popup (text is white by default)
async function showStatusPopup(text, color) {
    let statusPopup = document.getElementById("status-popup");
    statusPopup.style.backgroundColor = color;
    statusPopup.innerText = text;

    // Show popup
    statusPopup.style.display = "block";

    // Remove after 5 seconds
    await sleep(5000);
    statusPopup.style.display = "none";
}

/**
 * Main component for the Image to Image Generator
 *
 * @param {*} props
 * @returns
 */
function ImgToImg(props) {
    const [imgIsShown, setImgIsShown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [generationError, setGenerationError] = useState(false);
    const [generationErrorMsg, setGenerationErrorMsg] = useState("");

    const [advanced, setAdvanced] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [givenImg, setGivenImg] = useState(null);
    const csrftoken = useCookie("csrftoken");
    const user = useCurrentUser();
    const [seed, setSeed] = useState(1);
    const [guidance, setGuidance] = useState(7.5);
    const [steps, setSteps] = useState(15);
    var [negativePrompt, setNegativePrompt] = useState();

    // Most recent generated image
    const [generatedImg, setGeneratedImg] = useState();
    // Prompt used to produce most recent generated image
    const [generatedImgPrompt, setGeneratedImgPrompt] = useState();

    // Sets generator error message text depending on status and message
    // of response to /api/text-to-image requests
    // error: Axios error object
    const handleGeneratorErrors = (error) => {
        let errorStatus = error.response.status; // HTTP status
        let errorMessage = error.response.data.message; // Array of messages accompanying error status

        let message; // Message to display to user
        if (errorStatus == 404 && errorMessage.includes("Generator server not running.")) {
            let studentInsert = ""; // Text added if the user is a student
            if (!user.is_staff) {
                studentInsert = "ask your teacher to ";
            }
            message = "Generator is down. Please " + studentInsert + "reach out to the FRACTAL team and try again later.";
        } else if (errorStatus == 400 && errorMessage.includes("Prompt contains blocked words.")) {
            message = "Prompt contains inappropriate words. Rewrite your prompt and try again.";
        } else if (errorStatus == 400 && errorMessage.includes("Negative prompt contains blocked words.")) {
            message = "Negative prompt contains inappropriate words. Rewrite your prompt and try again.";
        } else if (errorStatus == 400) { // Less likely to occur because of frontend validation -> show errors in alert dialogue
            alert(errorMessage.map((error) => error + "\n"));
        } else {
            message = "Something went wrong. Try again later.";
        }

        // Show error message
        setLoading(false);
        setImgIsShown(false);
        setGenerationErrorMsg(message);
        setGenerationError(true);
    }

    const createFieldError = (text, input) => {
        let error = document.createElement("p");
        error.classList.add("field-error");
        error.innerText = text;
        input.after(error);
    }

    // Frontend validation of inputs
    // Returns a boolean value representing whether a generation can
    // occur (whether all fields are valid)
    const validateFields = () => {
        let readyToGenerate = true;

        // Inappropriate words are filtered from prompt on the backend
        let prompt = document.getElementById("prompt").value;
        let promptInput = document.getElementById("prompt");            
        if (prompt == null || prompt == "") {
            createFieldError("Prompt is missing.", promptInput);
            readyToGenerate = false;
        }

        let imageInput = document.getElementById("file-uploader");
        if (givenImg == null) {
            createFieldError("Image is missing.", imageInput);
            readyToGenerate = false;
        } else if (givenImg.indexOf("data:image/") !== 0) {
            createFieldError("Upload images only.", imageInput);
            readyToGenerate = false;
        }

        // Negative prompt is not required

        let seedInput = document.getElementById("Seed");
        if (isNaN(Number(seed))) {
            createFieldError("Seed must be a number.", seedInput);
            readyToGenerate = false;
        } else if (!Number.isInteger(Number(seed))) {
            createFieldError("Seed must be a whole number.", seedInput);
            readyToGenerate = false;
        } else if (seed < 0) {
            createFieldError("Seed cannot be negative.", seedInput);
            readyToGenerate = false;
        }

        let stepsInput = document.getElementById("Steps");
        if (isNaN(Number(steps))) {
            createFieldError("Steps must be a number.", stepsInput);
            readyToGenerate = false;
        } else if (!Number.isInteger(Number(steps))) {
            createFieldError("Steps must be a whole number.", stepsInput);
            readyToGenerate = false;
        } else if (steps < 0) {
            createFieldError("Steps must be a positive number.", stepsInput);
            readyToGenerate = false;
        } else if (steps < 1 || steps > 50) {
            createFieldError("Steps must be between 1 and 50.", stepsInput);
            readyToGenerate = false;
        }

        let guidanceInput = document.getElementById("GuidanceScale");
        if (isNaN(Number(guidance))) {
            createFieldError("Guidance must be a number.", guidanceInput);
            readyToGenerate = false;
        } else if (guidance < 0) {
            createFieldError("Guidance must be a positive number.", guidanceInput);
            readyToGenerate = false;
        }

        return readyToGenerate;
    }

    // When user presses "Save Image" button...
    async function handleSave() {
        // Disable Save button until save has been confirmed
        let saveImgBtn = document.getElementById("save-ai-img-btn");
        saveImgBtn.disabled = true;
        saveImgBtn.classList.add("locked");

        // Disable Generate button until save has been confirmed
        let generateBtn = document.getElementById("GenerateButton");
        generateBtn.disabled = true;
        generateBtn.classList.add("locked");

        // Add image to "Recent" student gallery and prompt to class where teacher can view
        await addImageToGallery(user, csrftoken, generatedImgPrompt, generatedImg);
    }

    const handleClick = useCallback(async () => {
        // Clear any field errors from previous generation attempt
        let fieldErrors = document.getElementsByClassName("field-error");
        Array.from(fieldErrors).forEach((fieldError) => { fieldError.remove() });

        let prompt = document.getElementById("prompt").value;

        let negative_prompt = "";
        if (advanced) {
            negative_prompt = document.getElementById("negative_prompt").value;
        }

        // If fields are invalid, do not go through with generation
        if (!validateFields()) {
            return;
        }

        // Disable "Generate" button while generation occurs...
        let generateButton = document.getElementById("GenerateButton");
        generateButton.disabled = true;
        generateButton.classList.add("locked");

        setLoading(true);
        setImgIsShown(false);
        setGenerationError(false);

        setImageUrl(null);

        try {
            // Generate image
            let img = await fetchImage(givenImg, prompt, negative_prompt, seed, guidance, steps, csrftoken);

            if (typeof img != "string") { // Unsuccessful generation; fetchImage returned error object
                handleGeneratorErrors(img);
                return;
            }
            
            // Remove loading spinner
            // Show generated image; do not show generation error
            setLoading(false);
            setImgIsShown(true);
            setGenerationError(false);

            // Set image and prompt used for generation
            setGeneratedImg(img);
            setGeneratedImgPrompt(prompt);

            // Render generated image
            setImageUrl('data:image/png;base64,' + img);
        } catch (error) {
            setLoading(false); // Remove loading spinner
            setImgIsShown(false); // Do not show image
            setGenerationError(true); // Show generation error
        } finally {
            generateButton.disabled = false; // Allow user to click "Generate" once more
            generateButton.classList.remove("locked");
        }
    }, [advanced, csrftoken, user, givenImg, seed, guidance, steps]);

    const handleImageUpload = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setGivenImg(reader.result);
        };
    };

    return (
        <div id="generation-area">
            <div id="status-popup"></div>
            <div style={{ width: "100%" }}>
                <p>Upload an image and use a prompt to generate something new!</p>
                <Stack direction="horizontal" gap={3}>
                    Prompt:
                    <Form.Control
                        id="prompt"
                        className="me-auto"
                        placeholder="Enter your prompt here..."
                    />
                    <Button
                        className="text-white"
                        variant="info"
                        onClick={() => handleClick()}
                        id="GenerateButton"
                    >
                        Generate
                    </Button>
                </Stack>
            </div>
            <div id="input-area">
                {advanced && (
                    <AdvancedOptions
                        negativePrompt={negativePrompt}
                        seed={seed}
                        setSeed={setSeed}
                        steps={steps}
                        setSteps={setSteps}
                        guidance={guidance}
                        setGuidance={setGuidance} />
                )}
                <div id="file-uploader"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}>
                    {loading ? (
                        <div className="blob-2" id="spinner">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    ) : imgIsShown ? (
                        <div id="ai-image-area">
                            <img
                                src={imageUrl}
                                id="generated-image"
                                alt="AI Art generation."
                            />
                            <Button
                                id="save-ai-img-btn"
                                variant="info"
                                className="text-white"
                                onClick={handleSave}>
                                    Save Image
                            </Button>
                        </div>                        
                    ) : generationError ? (
                        <div>
                            {generationErrorMsg}
                        </div>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "15px",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            <img src={givenImg}
                                style={{
                                    maxHeight: "256px",
                                    maxWidth: "256px"
                                }}
                            ></img>
                            <FileUploader
                                handleChange={handleImageUpload}
                                name="file"
                                types={["PNG", "JPEG"]}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div id="advopt-toggle">
                <Form.Check
                    type="switch"
                    id="AdvancedOptions"
                    label="Advanced Options"
                    value={advanced}
                    onClick={() => setAdvanced(!advanced)}
                />
                <span>
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip-disabled">
                                Switch for displaying the advanced options
                            </Tooltip>
                        }
                    >
                        <span style={{ marginLeft: "0.5vw" }}>ⓘ</span>
                    </OverlayTrigger>
                </span>
            </div>
        </div>
    );
}

export default ImgToImg;